'use client'

import { BottomSheet } from '@fukku/dialog/BottomSheet/BottomSheet'
import { Content } from '@fukku/dialog/Content/Content'
import type { DialogAPI } from '@fukku/dialog/Dialog'
import { Header } from '@fukku/dialog/Header/Header'
import {
	SideSheet,
	type SideSheetProps,
} from '@fukku/dialog/SideSheet/SideSheet'
import { useCartDispatch } from 'cart/context/CartProvider'

import { useShopTheLook } from 'cross-selling/contexts/ShopTheLookProvider/ShopTheLookProvider'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import dynamic from 'next/dynamic'
import type { SnackbarHandler } from 'notifications/components/Snackbar/Snackbar'
import { ProductLocations } from 'product/types/ProductLocations'
import {
	type ForwardRefExoticComponent,
	type RefAttributes,
	useCallback,
	useEffect,
	useRef,
} from 'react'
import { useResponsiveBehavior } from 'responsive/hooks/useResponsiveBehavior/useResponsiveBehavior'

const DynamicShopTheLookContent = dynamic(() =>
	import('./ShopTheLookContent/ShopTheLookContent').then(
		(mod) => mod.ShopTheLookContent
	)
)

type ShopTheLookCrossSellingProps = {
	readonly modalTitle: string
	readonly modalSubtitle?: string
	readonly modalText?: string
	readonly shopTheLookVariant?: ShopTheLookVariants
	readonly dataTestId?: string
	readonly location?: ProductLocations
}

export enum ShopTheLookVariants {
	Default = 'default',
	Similars = 'similars',
	BestSellers = 'best-sellers',
}

export function ShopTheLookCrossSelling({
	modalTitle,
	modalSubtitle,
	modalText,
	dataTestId,
	shopTheLookVariant = ShopTheLookVariants.Default,
	location = ProductLocations.ShopTheLook,
}: ShopTheLookCrossSellingProps): React.ReactNode {
	const sideSheetRef = useRef<DialogAPI>(null)
	const snackbarRef = useRef<SnackbarHandler | null>(null)
	const {
		country: { hasOnlineSale },
	} = useMasterData()
	const { isOpen, isLoading, setIsOpen, products } = useShopTheLook()
	const dispatch = useCartDispatch()

	const ResponsiveDialog = useResponsiveBehavior<
		ForwardRefExoticComponent<SideSheetProps & RefAttributes<DialogAPI>>
	>({
		default: SideSheet,
		small: BottomSheet,
	})

	const onClose = useCallback((): void => {
		setIsOpen(false)
		snackbarRef.current?.close()
	}, [])

	useEffect(() => {
		if (isOpen) {
			sideSheetRef.current?.show()
		} else {
			sideSheetRef.current?.close()
		}
	}, [isOpen, dispatch])

	return (
		<ResponsiveDialog
			ref={sideSheetRef}
			title={modalTitle}
			onClose={onClose}
			data-testid={dataTestId}
		>
			<Header title={modalTitle} subtitle={modalSubtitle} />
			<Content>
				<DynamicShopTheLookContent
					hasOnlineSale={hasOnlineSale}
					products={products}
					modalText={modalText}
					isLoading={isLoading}
					shopTheLookVariant={shopTheLookVariant}
					location={location}
				/>
			</Content>
		</ResponsiveDialog>
	)
}
